import { useFlueAgent } from '@flue/react';
import { useState } from 'react';
import { useSidebarPane } from '../components/PaneResizer.tsx';

/**
 * "Build with natural language."
 *
 * A chat with the Builder agent, which drafts a workflow document, validates it,
 * and saves it. It runs as the signed-in user, so the audit row carries their
 * name and the workflow it creates is theirs and private.
 *
 * The conversation id is the user id — one persistent builder session per
 * person, so a half-finished workflow is still there tomorrow.
 */
export function BuildWithLanguageView({
  userId,
  onWorkflowsChanged,
}: {
  userId: string;
  onWorkflowsChanged(): void;
}) {
  const agent = useFlueAgent({ url: `/api/agents/builder/${encodeURIComponent(userId)}` });
  const [draft, setDraft] = useState('');

  const send = async (text: string) => {
    if (!text.trim()) return;
    setDraft('');
    await agent.sendMessage(text);
    // A created workflow should appear in the sidebar without a manual reload.
    onWorkflowsChanged();
  };

  const pane = useSidebarPane('reef.language.sidebar', 260);

  return (
    <div {...pane.body}>
      <aside className="sidebar">
        <p className="section-title">Try</p>
        <div className="stack">
          {EXAMPLES.map((example) => (
            <button key={example} className="list-item" onClick={() => void send(example)}>
              {example}
            </button>
          ))}
        </div>
      </aside>

      {pane.resizer}

      <main className="main" style={{ display: 'grid', gridTemplateRows: 'auto 1fr', minHeight: 0 }}>
        <div>
          <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem' }}>Build with natural language</h1>
          <p className="hint" style={{ marginTop: 0 }}>
            Describe the automation. The builder drafts the workflow document, validates the step
            tree, shows it to you in plain language, and saves it once you agree. It cannot run
            anything — that stays a separate, deliberate action.
          </p>
        </div>

        <div className="chat" style={{ minHeight: 0 }}>
          <div />
          <div className="chat-log">
            {agent.messages.length === 0 && (
              <div className="empty">
                Start with what triggers it. “When a Linear issue is labelled security, have an
                agent review the affected code and post a summary to #sec-review.”
              </div>
            )}

            {agent.messages.map((message, messageIndex) => (
              <div key={messageIndex} className={`msg ${message.role === 'user' ? 'user' : ''}`}>
                <div className="role">{message.role === 'user' ? 'you' : 'builder'}</div>
                {message.parts.map((part, partIndex) =>
                  part.type === 'text' ? <span key={partIndex}>{part.text}</span> : null,
                )}
              </div>
            ))}
          </div>

          <div className="chat-input">
            <textarea
              rows={3}
              value={draft}
              placeholder="Describe the workflow…"
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault();
                  void send(draft);
                }
              }}
            />
            <div className="row">
              <span className="hint">⌘↵ to send</span>
              <span className="spacer" />
              <button className="primary" onClick={() => void send(draft)} disabled={!draft.trim()}>
                Send
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

const EXAMPLES = [
  'When a Linear issue moves to triage, plan the change against our code standards and post it to #eng for approval.',
  'Every Monday at 9am, summarise last week’s merged PRs and post it to #eng-weekly.',
  'When a PR is labelled needs-review, review it with pr-reviewer-v2 and comment the findings.',
  'On a Signoz alert, pull the last hour of traces for the affected service and open a Linear issue.',
];
